<script>
// --- LÓGICA ORIGINAL (COMPLETA Y FUNCIONAL) ---
import { authMethods, notificationMethods } from "@/state/helpers";
import { mapState } from "vuex";
import { required, email } from "vuelidate/lib/validators";
import { renderizarBotonGoogle } from "@/services/googleIdentity";

export default {
  // Quitamos el layout anterior para usar el nuevo diseño de página completa
  // components: { Layout }, 
  data() {
    return {
      email: "",
      password: "",
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      googleError: null
    };
  },
  validations: {
    email: { required, email },
    password: { required }
  },
  computed: {
    ...mapState("authfack", ["status"]),
    notification() {
      return this.$store ? this.$store.state.notification : null;
    }
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    /**
     * Los gestores de contraseñas y el autocompletado escriben directamente en
     * el DOM, sin disparar el evento que escucha `v-model`: el campo se ve
     * lleno pero el componente lo cree vacío y la validación lo rechaza.
     */
    sincronizarAutocompletado() {
      const campos = [
        { ref: "campoEmail", modelo: "email" },
        { ref: "campoPassword", modelo: "password" },
      ];
      for (const { ref, modelo } of campos) {
        const componente = this.$refs[ref];
        const elemento = componente && componente.$el;
        if (elemento && elemento.value !== this[modelo]) this[modelo] = elemento.value;
      }
    },

    tryToLogIn() {
      this.sincronizarAutocompletado();
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        // Asumimos que siempre usas Firebase
        this.tryingToLogIn = true;
        this.authError = null;

        this.logIn({ email: this.email, password: this.password })
          .then(() => this.entrarSiEsEmpleado())
          .catch(error => this.manejarError(error));
      }
    },

    /** Llega el token de identidad desde el botón de Google. */
    entrarConGoogle(idToken) {
      this.tryingToLogIn = true;
      this.authError = null;

      this.logInConCredencialGoogle(idToken)
        .then(() => this.entrarSiEsEmpleado())
        .catch(error => this.manejarError(error));
    },

    prepararBotonGoogle() {
      renderizarBotonGoogle(this.$refs.botonGoogle, this.entrarConGoogle)
        .catch((error) => {
          // Si Google no carga, el acceso con correo y contraseña sigue estando.
          console.error("[login] no se pudo preparar el acceso con Google:", error);
          this.googleError = "El acceso con Google no está disponible en este momento.";
        });
    },

    /**
     * Tramo común de los dos accesos. La verificación vive en el módulo
     * `empleado` para que el guard de rutas la repita con el mismo criterio.
     */
    entrarSiEsEmpleado() {
      return this.$store.dispatch("empleado/cargar", { force: true })
        .then((empleado) => {
          if (empleado) {
            this.tryingToLogIn = false;
            this.isAuthError = false;
            this.$router.push(this.$route.query.redirectFrom || { name: "default" });
          } else {
            return this.logOut().then(() => {
              this.$store.dispatch("empleado/limpiar");
              this.tryingToLogIn = false;
              this.authError = "Esta cuenta no tiene permisos de administrador";
              this.isAuthError = true;
            });
          }
        });
    },

    manejarError(error) {
      this.tryingToLogIn = false;
      console.error("[login] fallo al iniciar sesión:", error);
      // Mensajes de error más amigables para el usuario
      switch (error && error.code) {
        case 'auth/user-not-found':
          this.authError = "El usuario no fue encontrado.";
          break;
        case 'auth/wrong-password':
          this.authError = "La contraseña es incorrecta.";
          break;
        case 'auth/unauthorized-domain':
          this.authError = "Este dominio no está autorizado en Firebase para entrar con Google.";
          break;
        default:
          this.authError = "Error al iniciar sesión: " + ((error && error.message) || error);
      }
      this.isAuthError = true;
    }
  },
  mounted() {
    // Añadimos una clase al body para el fondo, si es necesario
    document.body.classList.add("auth-body-bg");

    this.prepararBotonGoogle();
  },
  destroyed() {
    // Limpiamos la clase al salir del componente
    document.body.classList.remove("auth-body-bg");
  }
};
</script>

<template>
  <div>
    <BContainer fluid class="p-0">
      <BRow class="g-0">
        <BCol xl="9">
          <div class="auth-full-bg">
            <div class="bg-overlay"></div>

            <div class="auth-text-bottom">
              <BRow class="justify-content-center">
                <BCol lg="9">
                  <div class="text-center">
                    <h1 class="mb-3 text-white">
                      Gestión y Control para tu Red
                    </h1>
                    <p class="text-white-50">
                      Plataforma interna para la administración de recursos y personal de Reconstrucción Network.
                    </p>
                  </div>
                </BCol>
              </BRow>
            </div>

          </div>
        </BCol>

        <BCol xl="3">
          <div class="auth-full-page-content p-md-5 p-4">
            <div class="w-100">
              <img :src="require('@/assets/images/RCCN-LOGO-WHITE.png')" alt="Sesiones de reconstrucción"
                class="img-fluid w-100 rounded shadow-sm"
                style="max-height: 260px; object-fit: cover; object-position: center;" />
              <div class="d-flex flex-column h-100">
                <div class="my-auto">
                  <div>
                    <h5 class="text-white">¡Bienvenido de nuevo!</h5>
                    <p class="text-white">Inicia sesión para continuar.</p>
                  </div>

                  <div class="mt-4">
                    <BAlert :model-value="isAuthError" variant="danger" dismissible>{{ authError }}</BAlert>
                    <div v-if="notification.message" :class="'alert ' + notification.type">{{ notification.message }}
                    </div>

                    <BForm @submit.prevent="tryToLogIn">
                      <BFormGroup class="mb-3 text-white" label="Usuario" label-for="email-input">
                        <BFormInput id="email-input" ref="campoEmail" v-model="email" type="text"
                          placeholder="Introduce tu email" autocomplete="username"
                          :class="{ 'is-invalid': submitted && $v.email.$error }"></BFormInput>
                        <div v-if="submitted && $v.email.$error" class="invalid-feedback">
                          <span v-if="!$v.email.required">El email es obligatorio.</span>
                          <span v-if="!$v.email.email">Por favor, introduce un email válido.</span>
                        </div>
                      </BFormGroup>

                      <BFormGroup class="mb-3 text-white" label="Contraseña" label-for="password-input">
                        <BFormInput id="password-input" ref="campoPassword" v-model="password" type="password"
                          placeholder="Introduce tu contraseña" autocomplete="current-password"
                          :class="{ 'is-invalid': submitted && $v.password.$error }"></BFormInput>
                        <div v-if="submitted && !$v.password.required" class="invalid-feedback">La contraseña es
                          obligatoria.</div>
                      </BFormGroup>

                      <div class="mt-3 d-grid">
                        <BButton type="submit" variant="primary" :disabled="tryingToLogIn">
                          <span v-if="!tryingToLogIn">Entrar</span>
                          <span v-else>
                            <i class="bx bx-loader-alt bx-spin"></i> Verificando...
                          </span>
                        </BButton>
                      </div>
                    </BForm>

                    <div class="separador-google"><span>o</span></div>

                    <!-- Google dibuja aquí su propio botón: es requisito de sus
                         pautas de marca y es lo que permite recibir el token
                         sin salir de esta página. -->
                    <div ref="botonGoogle" class="contenedor-google"></div>

                    <p v-if="googleError" class="text-white-50 small mt-2 mb-0">{{ googleError }}</p>
                  </div>
                </div>

                <div class="mt-4 text-center">
                  <router-link :to="{ name: 'portal-login' }" class="text-white-50">
                    ¿Eres miembro de Recnet? Entra aquí
                  </router-link>
                </div>

                <div class="mt-4 mt-md-5 text-center">
                  <p class="mb-0 text-white">
                    © {{ new Date().getFullYear() }} Reconstrucción Network
                  </p>
                </div>

              </div>
            </div>
          </div>
        </BCol>
      </BRow>
    </BContainer>
  </div>
</template>

<style lang="scss">
// Estilos para el nuevo diseño
.auth-body-bg {
  background-color: #e2e1e0;
}

.separador-google {
  display: flex;
  align-items: center;
  margin: 20px 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
  }

  span {
    padding: 0 12px;
  }
}

// El botón lo inyecta Google dentro de este contenedor; sólo se centra.
.contenedor-google {
  display: flex;
  justify-content: center;
  min-height: 44px;
}

.auth-full-bg {
  background-image: url('~@/assets/images/RCNT-BANNER1.png'); // Asegúrate de tener una imagen de fondo en esta ruta
  background-size: cover;
  background-position: center;
  height: 100vh;
  position: relative; // Asegura que este sea el contenedor para el posicionamiento absoluto
  display: flex; // Usamos flexbox para controlar la posición del contenido
  flex-direction: column; // Alineamos los elementos verticalmente
  justify-content: flex-end; // Empuja el contenido al final (abajo)

  .bg-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(64, 73, 83, 0.7);
  }
}

.auth-full-page-content {
  height: 100vh;
  background-color: #5c2e84;
  padding: 0 2rem 5rem 2rem; // Añadimos un poco de padding inferior
  text-align: center; // Centramos el texto
}
</style>